import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '../../components/ui/table';
import { Building2, Key, Copy, Plus, KeyRound, RefreshCw } from 'lucide-react';

const authHeaders = () => {
  const token = localStorage.getItem('jwt') || localStorage.getItem('adminToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

const AdminClients = () => {
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientName, setClientName] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [generatedKey, setGeneratedKey] = useState('');
  const [tempPassword, setTempPassword] = useState(null); // { email, password }
  const [creating, setCreating] = useState(false);
  const [busyClientId, setBusyClientId] = useState('');

  const showMessage = (text, type = 'info') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const loadClients = useCallback(async () => {
    setClientsLoading(true);
    try {
      const response = await fetch('/api/admin/clients', { headers: authHeaders() });
      const data = await response.json();
      if (response.ok) {
        setClients(data.clients || []);
      } else {
        showMessage(data.detail || 'Failed to load clients', 'error');
      }
    } catch (error) {
      showMessage(`Error: ${error.message}`, 'error');
    } finally {
      setClientsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const createClient = async () => {
    if (!clientName.trim()) {
      showMessage('Please enter a client name', 'error');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch('/api/admin/clients', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ name: clientName }),
      });
      const data = await response.json();

      if (response.ok) {
        showMessage(`Created client: ${data.client.name} (${data.client.id})`, 'success');
        setClientName('');
        loadClients();
      } else {
        showMessage(data.detail || 'Failed to create client', 'error');
      }
    } catch (error) {
      showMessage(`Error: ${error.message}`, 'error');
    } finally {
      setCreating(false);
    }
  };

  const generateApiKey = async (clientId) => {
    setBusyClientId(clientId);
    setGeneratedKey('');
    setTempPassword(null);
    try {
      const response = await fetch(`/api/admin/clients/${encodeURIComponent(clientId)}/keys`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await response.json();

      if (response.ok) {
        showMessage('API key generated successfully!', 'success');
        setGeneratedKey(data.plaintext_api_key);
        loadClients();
      } else {
        showMessage(data.detail || 'Failed to generate API key', 'error');
      }
    } catch (error) {
      showMessage(`Error: ${error.message}`, 'error');
    } finally {
      setBusyClientId('');
    }
  };

  const resetOwnerPassword = async (client) => {
    const ok = window.confirm(
      `Set a new temporary password for the owner of "${client.name}"`
      + (client.owner_email ? ` (${client.owner_email})?` : '?')
      + ' Their current password will stop working.'
    );
    if (!ok) return;

    setBusyClientId(client.id);
    setTempPassword(null);
    try {
      const response = await fetch(`/api/admin/clients/${encodeURIComponent(client.id)}/reset-password`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await response.json();

      if (response.ok) {
        showMessage('Temporary password generated.', 'success');
        setGeneratedKey('');
        setTempPassword({ email: data.owner_email, password: data.temporary_password });
      } else {
        showMessage(data.detail || 'Failed to reset password', 'error');
      }
    } catch (error) {
      showMessage(`Error: ${error.message}`, 'error');
    } finally {
      setBusyClientId('');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      showMessage('Copied to clipboard!', 'success');
    });
  };

  const formatDate = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleDateString();
    } catch {
      return value;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Client Management</h1>
          <p className="text-muted-foreground">
            View all clients, issue API keys, and reset owner credentials
          </p>
        </div>
        <Button variant="outline" onClick={loadClients} disabled={clientsLoading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${clientsLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`p-4 rounded-md ${
          messageType === 'error' ? 'bg-destructive/10 text-destructive' :
          messageType === 'success' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
          'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
        }`}>
          {message}
        </div>
      )}

      {/* Generated API Key Display */}
      {generatedKey && (
        <Card className="border-green-200 dark:border-green-800">
          <CardHeader>
            <CardTitle className="text-green-700 dark:text-green-400">
              🔑 API Key Generated
            </CardTitle>
            <CardDescription className="text-red-600 dark:text-red-400">
              ⚠️ Copy and store this key securely. It won't be shown again.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-muted rounded-md font-mono text-sm break-all">
              {generatedKey}
            </div>
            <Button
              onClick={() => copyToClipboard(generatedKey)}
              variant="outline"
              className="w-full"
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy API Key
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Temporary Password Display */}
      {tempPassword && (
        <Card className="border-green-200 dark:border-green-800">
          <CardHeader>
            <CardTitle className="text-green-700 dark:text-green-400">
              🔐 Temporary Password Set
            </CardTitle>
            <CardDescription className="text-red-600 dark:text-red-400">
              ⚠️ Share this securely with {tempPassword.email || 'the owner'}. It won't be shown again, and their old password no longer works.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-muted rounded-md font-mono text-sm break-all">
              {tempPassword.password}
            </div>
            <Button
              onClick={() => copyToClipboard(tempPassword.password)}
              variant="outline"
              className="w-full"
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy Password
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create Client */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Building2 className="h-5 w-5" />
            <span>Create New Client</span>
          </CardTitle>
          <CardDescription>
            Add a new client to the multi-tenant system
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1 space-y-2">
              <Label htmlFor="clientName">Client Name</Label>
              <Input
                id="clientName"
                placeholder="Enter client name (e.g., Luxury Hotels Group)"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                disabled={creating}
                onKeyDown={(e) => e.key === 'Enter' && createClient()}
              />
            </div>
            <Button onClick={createClient} disabled={creating || !clientName.trim()}>
              {creating ? 'Creating...' : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Client
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* All Clients */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Building2 className="h-5 w-5" />
            <span>All Clients</span>
            {!clientsLoading && (
              <Badge variant="secondary">{clients.length}</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Every client in the system, with brands, hotels, users and active API keys
          </CardDescription>
        </CardHeader>
        <CardContent>
          {clientsLoading ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Loading clients…</p>
          ) : clients.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No clients yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead className="text-center">Brands</TableHead>
                  <TableHead className="text-center">Hotels</TableHead>
                  <TableHead className="text-center">Users</TableHead>
                  <TableHead className="text-center">Active Keys</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map((client) => (
                  <TableRow key={client.id}>
                    <TableCell>
                      <div className="font-medium">{client.name}</div>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(client.id)}
                        className="text-xs text-muted-foreground font-mono hover:text-foreground flex items-center gap-1"
                        title="Copy client ID"
                      >
                        {client.id}
                        <Copy className="h-3 w-3" />
                      </button>
                    </TableCell>
                    <TableCell>
                      {client.owner_email ? (
                        <div>
                          <div className="text-sm">{client.owner_name || '—'}</div>
                          <div className="text-xs text-muted-foreground">{client.owner_email}</div>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">No owner</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">{client.brands_count}</TableCell>
                    <TableCell className="text-center">{client.hotels_count}</TableCell>
                    <TableCell className="text-center">{client.users_count}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant={client.active_keys_count > 0 ? 'default' : 'secondary'}>
                        {client.active_keys_count}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(client.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyClientId === client.id}
                          onClick={() => generateApiKey(client.id)}
                        >
                          <Key className="h-4 w-4 mr-1" />
                          API Key
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyClientId === client.id || !client.owner_email}
                          onClick={() => resetOwnerPassword(client)}
                          title={client.owner_email ? 'Send password reset email to owner' : 'No owner to reset'}
                        >
                          <KeyRound className="h-4 w-4 mr-1" />
                          Reset Password
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>Integration Instructions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-medium mb-2">Using the API Key:</h4>
            <div className="p-4 bg-muted rounded-md font-mono text-sm">
              <div>curl -H "X-Client-Key: YOUR_API_KEY" \</div>
              <div className="ml-4">https://localdemand.thinkrobin.com:8443/api/demand</div>
            </div>
          </div>
          <div>
            <h4 className="font-medium mb-2">Available Endpoints:</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• GET /api/demand - Get demand forecasts</li>
              <li>• GET /api/locations - List supported locations</li>
              <li>• GET /api/stats - API usage statistics</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminClients;
